import { BasePageComponent } from "@/components/base/BasePageComponent";
import { ConfirmDialogComponent } from "@/components/confirm-dialog/confirm-dialog.component";
import { DataLoadingComponent } from "@/components/data-loading/data-loading.component";
import { PageTitleComponent } from "@/components/page-title/page-title.component";
import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { DateFormatPipe } from "@/extensions/datePipe";
import { Paging } from "@/models/Paging";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { NavService } from "@/services/nav.service";
import { NotificationCheckService } from "@/services/notification-check.service";
import { DialogModule } from "@angular/cdk/dialog";
import { NgFor, NgIf } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { NotificationUser, NotificationsService } from "@eemo/api-client";
import { TranslateModule } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { DateTimeFormatPipe } from "../../extensions/dateTimePipe";

export type NotificationUserMutable = {
  -readonly [K in keyof NotificationUser]: NotificationUser[K];
};

interface NotificationUserSafe {
  item: NotificationUserMutable;
  safeContent: SafeHtml;
}

@Component({
  selector: "app-notifications",
  templateUrl: "./notifications.component.html",
  styleUrl: "./notifications.component.scss",
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    PageTitleComponent,
    DateFormatPipe,
    DateTimeFormatPipe,
    DataLoadingComponent,
    TranslateModule,
    DialogModule,
    TooltipDirective,
  ],
})
export class NotificationsComponent
  extends BasePageComponent
  implements OnInit, OnDestroy
{
  @ViewChild(MatPaginator) paginator: MatPaginator;

  selectedUid = "";
  loading = false;
  curNotificationIndex = -1;
  notifications: NotificationUserSafe[] = [];
  routeSub = new Subscription();
  getSub = new Subscription();
  readSub = new Subscription();
  deleteSub = new Subscription();
  deleteAllSub = new Subscription();
  readAllSub = new Subscription();

  isMobile = false;

  paging = new Paging();

  constructor(
    protected route: ActivatedRoute,
    private router: Router,
    private appConfigService: AppConfigService,
    private notificationCheckService: NotificationCheckService,
    private notifcationsService: NotificationsService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    protected navService: NavService
  ) {
    super(route, navService);
  }
  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const uid = params.get("uid");
      if (uid) {
        this.selectedUid = uid;
      }
    });
    this.isMobile = this.appConfigService.isMobileOrTablet;
    this.loadData();
  }

  ngOnDestroy(): void {
    this.routeSub.unsubscribe();
    this.getSub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.readSub.unsubscribe();
    this.deleteAllSub.unsubscribe();
    this.readAllSub.unsubscribe();
  }

  loadData(): void {
    this.loading = true;
    this.getSub = this.notifcationsService
      .getCurrentUserNotifications(
        this.paging.offset,
        this.paging.pagingPageSize
      )
      .subscribe({
        next: (data) => {
          // mark content as safe
          const list: NotificationUserSafe[] = [];
          data.list.forEach((n) => {
            list.push({
              item: n,
              safeContent: this.sanitizer.bypassSecurityTrustHtml(n.content),
            });
          });
          this.notifications = list;
          this.paging.length = data.paging.totalItems;
          //select the notification if provided
          if (this.selectedUid) {
            this.notifications.forEach((n, i) => {
              if (n.item.uid === this.selectedUid) {
                this.readNotification(i);
              }
            });
          }
        },
        complete: () => (this.loading = false),
      });
  }

  handlePageEvent(e: PageEvent) {
    this.paging.handlePageEvent(e);
    this.loadData();
  }

  readNotification(index: number) {
    this.curNotificationIndex = index;
    const notif = this.findFromIndex(index);
    if (!notif.read) {
      this.readSub = this.notifcationsService
        .readCurrentUserNotification(notif.uid)
        .subscribe();
      this.notificationCheckService.decreaseNotification();
      notif.read = true;
    }
  }

  nextNotification() {
    this.curNotificationIndex++;
  }

  onDeleteNotification(index: number) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Supprimer la notification ?",
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteNotification(index);
        }
      });
    }
  }

  doDeleteNotification(index: number) {
    this.deleteSub = this.notifcationsService
      .deleteCurrentUserNotification(this.findFromIndex(index).uid)
      .subscribe(() => {
        this.alertService.success("Notification supprimée");
        this.loadData();
      });
  }

  onDeleteAll() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Supprimer toutes les notifications ?",
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteAll();
        }
      });
    }
  }

  doDeleteAll() {
    this.deleteAllSub = this.notifcationsService
      .deleteCurrentUserAllNotifications()
      .subscribe(() => {
        this.alertService.success("Notifications supprimées");
        this.notificationCheckService.notificationCount.next(0);
        this.loadData();
      });
  }

  onReadAll() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Marquer toutes les notifications comme lues ?",
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doReadAll();
        }
      });
    }
  }

  doReadAll() {
    this.readAllSub = this.notifcationsService
      .readCurrentUserAllNotifications()
      .subscribe(() => {
        this.alertService.success("Notifications marquées comme lues");
        this.notificationCheckService.notificationCount.next(0);
        this.loadData();
      });
  }

  goToLink(index: number) {
    const url = new URL(this.findFromIndex(index).link);
    this.router.navigateByUrl(url.pathname);
  }

  findFromIndex(index: number): NotificationUserMutable {
    return this.notifications[index].item;
  }
}
